import React, { useState } from 'react'
import { useSeo } from '../../lib/i18n'

export default function KontaktEn() {
  useSeo({
    title: 'Contact - Borgs Driving School | Kalundborg',
    description: 'Contact Borgs Driving School in Kalundborg. Call 21 47 01 87 / 23 20 04 25 or send a message to register for our next driving course.',
    lang: 'en',
    pathname: '/en/contact'
  })

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Mandatory honeypot field
  })
  const [status, setStatus] = useState({ type: null, message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus({ type: null, message: '' })

    // Validation check
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus({ type: 'error', message: 'Please fill in all required fields.' })
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url // Honeypot
        })
      })

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Thank you for your message! We will get back to you as soon as possible.'
        })
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        })
      } else {
        setStatus({
          type: 'error',
          message: 'An error occurred during submission. Please call us directly at 21 47 01 87 or 23 20 04 25.'
        })
      }
    } catch (error) {
      console.error('Webform submission error:', error)
      setStatus({
        type: 'error',
        message: 'Network error. Please call us directly at 21 47 01 87 or 23 20 04 25.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-left max-w-3xl mb-16">
          <h1 className="font-header text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Contact Borgs Driving School
          </h1>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            Have questions about course start dates, driving lessons, or pricing? Call us directly or fill in the form below.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Contact Details Card */}
          <div className="lg:col-span-5 bg-emerald-900 text-white p-8 sm:p-10 shadow-xl space-y-8">
            <div>
              <span className="text-white text-xs font-bold uppercase tracking-widest block mb-1">
                Direct Contact
              </span>
              <h2 className="font-header text-2xl sm:text-3xl font-bold text-white">
                Borgs Driving School I/S
              </h2>
              <p className="text-white text-sm mt-2 leading-relaxed">
                Your local driving school with attentive, high-quality instruction in Kalundborg.
              </p>
            </div>

            <div className="divide-y divide-emerald-800/80 text-sm">
              <div className="flex items-start gap-4 pb-5">
                <i className="fa-solid fa-phone text-white text-xl flex-shrink-0 mt-1" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-white uppercase font-bold block">Phone</span>
                  <div className="flex flex-col sm:flex-row sm:gap-3 gap-1 mt-1">
                    <a
                      href="tel:+4521470187"
                      className="text-white hover:text-emerald-200 font-bold transition-colors"
                    >
                      21 47 01 87 (Kim)
                    </a>
                    <span className="text-emerald-300 hidden sm:inline">|</span>
                    <a
                      href="tel:+4523200425"
                      className="text-white hover:text-emerald-200 font-bold transition-colors"
                    >
                      23 20 04 25 (Hanne)
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 py-5">
                <i className="fa-solid fa-envelope text-white text-xl flex-shrink-0 mt-1" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-white uppercase font-bold block">Email</span>
                  <a
                    href="mailto:info@borgskoereskole.dk"
                    className="text-white hover:text-emerald-200 font-medium transition-colors block mt-1"
                  >
                    info@borgskoereskole.dk
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 py-5">
                <i className="fa-solid fa-location-dot text-white text-xl flex-shrink-0 mt-1" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-white uppercase font-bold block">Addresses</span>
                  <p className="text-white mt-1">
                    <strong>Theory &amp; Office:</strong><br />
                    Tornemarksvej 10B, 4593 Eskebjerg
                  </p>
                  <p className="text-white mt-2">
                    <strong>Driving lessons pickup:</strong><br />
                    Kalundborg and surrounding areas
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 pt-5">
                <i className="fa-solid fa-building text-white text-xl flex-shrink-0 mt-1" aria-hidden="true"></i>
                <div>
                  <span className="text-xs text-white uppercase font-bold block">Company Info</span>
                  <p className="text-white mt-1">
                    Borgs Køreskole I/S<br />
                    CVR: 35 15 48 94
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-emerald-800">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-xs font-semibold text-white">
                  Open for new course registrations
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 bg-white p-8 sm:p-10 border border-slate-200 shadow-sm">
            <h2 className="font-header text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
              Send us a message
            </h2>

            {status.message && (
              <div
                className={`p-4 mb-6 text-sm font-medium ${
                  status.type === 'success'
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - sr-only */}
              <div className="sr-only absolute -left-[9999px]">
                <label htmlFor="website_url">Website URL</label>
                <input
                  type="text"
                  name="website_url"
                  id="website_url"
                  tabIndex="-1"
                  autoComplete="off"
                  value={formData.website_url}
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="E.g. John Doe"
                    className="w-full px-4 py-3 border border-slate-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none transition-colors text-slate-900 placeholder-slate-400 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="E.g. 12 34 56 78"
                    className="w-full px-4 py-3 border border-slate-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none transition-colors text-slate-900 placeholder-slate-400 text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  className="w-full px-4 py-3 border border-slate-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none transition-colors text-slate-900 placeholder-slate-400 text-sm"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  Message / Registration <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  id="message"
                  rows={5}
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us when you want to start, your age, or ask any questions you have..."
                  className="w-full px-4 py-3 border border-slate-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none transition-colors text-slate-900 placeholder-slate-400 text-sm resize-y"
                ></textarea>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider text-sm shadow-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-circle-notch fa-spin"></i>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane"></i>
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

        </div>

      </div>
    </div>
  )
}
